/**
 * API Call Deduplication Utility
 * Prevents multiple identical API calls from being made simultaneously
 */

interface PendingCall {
  promise: Promise<any>;
  timestamp: number;
}

class ApiDeduplicationService {
  private pendingCalls: Map<string, PendingCall> = new Map();
  private readonly CACHE_DURATION = 5000; // 5 seconds

  /**
   * Generate a unique key for an API call
   */
  private generateKey(url: string, method: string, params?: any): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${method.toUpperCase()}:${url}:${paramString}`;
  }

  /**
   * Check if a call is already pending
   */
  private isCallPending(key: string): boolean {
    const pendingCall = this.pendingCalls.get(key);
    if (!pendingCall) return false;

    // Remove expired calls
    if (Date.now() - pendingCall.timestamp > this.CACHE_DURATION) {
      this.pendingCalls.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Execute an API call with deduplication
   */
  async execute<T>(
    url: string,
    method: string,
    apiCall: () => Promise<T>,
    params?: any
  ): Promise<T> {
    const key = this.generateKey(url, method, params);

    // If call is already pending, return the existing promise
    if (this.isCallPending(key)) {
      const pendingCall = this.pendingCalls.get(key);
      if (pendingCall) {
        console.log(`🔄 Deduplicating API call: ${key}`);
        return pendingCall.promise;
      }
    }

    // Create new call
    const promise = apiCall().finally(() => {
      // Remove from pending calls when completed
      this.pendingCalls.delete(key);
    });

    // Store the pending call
    this.pendingCalls.set(key, {
      promise,
      timestamp: Date.now()
    });

    console.log(`🚀 New API call: ${key}`);
    return promise;
  }

  /**
   * Clear all pending calls
   */
  clear(): void {
    this.pendingCalls.clear();
  }

  /**
   * Get current pending calls count
   */
  getPendingCallsCount(): number {
    return this.pendingCalls.size;
  }
}

// Export singleton instance
export const apiDeduplicationService = new ApiDeduplicationService();
export default apiDeduplicationService;
